import { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "sonner";
import { CheckCircle2, MessageSquare, Clock, AlertTriangle, CalendarOff, Receipt } from "lucide-react";
import { getMessages } from "../../api/api";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const TABS = [
  { key: "all", label: "All", type: null },
  { key: "confirmation", label: "Confirmations", type: "confirmation" },
  { key: "reminder", label: "Reminders", type: "reminder" },
  { key: "delay", label: "Delay alerts", type: "delay" },
  { key: "leave", label: "Leave alerts", type: "leave" },
  { key: "invoice", label: "Invoices", type: "invoice" },
];

const TYPE_ICON = {
  confirmation: CheckCircle2,
  reminder: Clock,
  delay: AlertTriangle,
  leave: CalendarOff,
  invoice: Receipt,
};

const TYPE_LABEL = {
  confirmation: "Confirmation",
  reminder: "Reminder",
  delay: "Delay alert",
  leave: "Leave alert",
  invoice: "Invoice",
};

export default function Messages() {
  const [tab, setTab] = useState("all");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const type = TABS.find((t) => t.key === tab)?.type;
    getMessages({ type: type || undefined, limit: 50 })
      .then((res) => setMessages(res.data))
      .catch((err) => toast.error(err?.message || "Could not load messages."))
      .finally(() => setLoading(false));
  }, [tab]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-serif text-2xl font-semibold">Patient messages</h2>
        <p className="text-sm text-muted-foreground">
          WhatsApp delivery log — confirmations, reminders, delay & leave alerts
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-2">
        {loading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No messages yet.</p>
        ) : (
          messages.map((m) => {
            const Icon = TYPE_ICON[m.type] || MessageSquare;
            return (
              <div key={m._id} className="flex gap-3 rounded-lg border border-border bg-card p-4">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {TYPE_LABEL[m.type]} · {m.patient_id?.name} · {m.patient_id?.phone}
                    </p>
                    <p className="text-xs text-muted-foreground">{moment(m.createdAt).fromNow()}</p>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">{m.text}</p>
                  <p className="mt-2 flex items-center gap-1 text-xs font-medium text-secondary">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Delivered via WhatsApp
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
